'use client';

import { cn, numberToString, trimBreakLines } from 'common/utils';
import RoundIcon from 'components/common/RoundIcon';
import { format } from 'date-fns';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useMutation } from 'react-query';
import { ForumCommentType, likeForumComment } from 'service/forum';

import { isMyComment } from 'common/utils/forum';
import RichContent from 'components/common/RichContent';
import { LikeIcon } from 'components/v2/atoms/icon';
import ConfirmDialog from 'components/v2/molecules/confirm-dialog';
import useStore from 'store/timeline';
import ForumThreeDotMenu from './forum-three-dot-menu';

interface ForumCommentItemPropsType {
  comment: ForumCommentType;
  profileUserId: number;
  profileUsername: string;
  onDelete: (commentId: number) => void;
  isDetail?: boolean;
  isReply?: boolean;
}

const ForumCommentItem = ({
  comment,
  profileUserId,
  profileUsername,
  onDelete,
  isDetail = false,
  isReply = false,
}: ForumCommentItemPropsType) => {
  const router = useRouter();
  const [likeCount, setLikeCount] = useState<number>(comment.like_count || 0);
  const [isLiked, setIsLiked] = useState<boolean>(comment.is_liked || false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const currentUser = useStore((state) => state.currentUser);
  const formattedDate = format(new Date(comment.created_at), 'yyyy/M/d HH:mm');

  // Like/unlike mutation
  const { mutate: toggleLike, isLoading: isLiking } = useMutation<
    void,
    unknown,
    'like' | 'unlike'
  >({
    mutationFn: async (action) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      await likeForumComment(profileUserId, comment.id, action);
    },
    onMutate: (action) => {
      // Optimistic update
      if (action === 'like') {
        setLikeCount((prev) => prev + 1);
        setIsLiked(true);
      } else {
        setLikeCount((prev) => Math.max(0, prev - 1));
        setIsLiked(false);
      }
    },
    onError: (_, action) => {
      // Rollback on error
      if (action === 'like') {
        setLikeCount((prev) => Math.max(0, prev - 1));
        setIsLiked(false);
      } else {
        setLikeCount((prev) => prev + 1);
        setIsLiked(true);
      }
    },
  });

  const handleLike = () => {
    if (isLiking) return;
    const action = isLiked ? 'unlike' : 'like';
    toggleLike(action);
  };

  // Navigate to comment detail (for list view)
  const handleReplyClick = () => {
    if (!isDetail) {
      void router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, comment_id: comment.id },
        },
        undefined,
        { shallow: true },
      );
    }
  };

  return (
    <div
      className={cn(
        'rounded-xl bg-white p-4',
        isDetail ? 'border border-gray-200' : '',
      )}
    >
      {/* Header row */}
      <div className="flex items-center gap-3">
        {/* Avatar */}
        <Link href={`/${comment.user_info?.username}`} className="shrink-0">
          <RoundIcon user={comment.user_info} width={40} height={40} hasRing />
        </Link>

        {/* Username masked */}
        <Link
          href={`/${comment.user_info?.username}`}
          className="font-semibold text-pp-text-primary hover:underline"
        >
          {comment.user_info?.name}
        </Link>

        {/* Comment number - aligned right */}
        <div className="ml-auto flex items-center gap-2">
          {isDetail ? (
            <span className="text-sm text-pp-text-secondary">
              No.{comment.comment_number_id}
            </span>
          ) : (
            <Link
              href={`/${profileUsername}?tab=forum&comment_id=${comment.id}`}
              shallow
              className="text-sm text-sys-blue-primary hover:underline"
            >
              No.{comment.comment_number_id}
            </Link>
          )}

          {/* Three dot menu - only show for own comments */}
          {isMyComment(comment, currentUser) && (
            <ForumThreeDotMenu onDelete={() => setOpenConfirmDelete(true)} />
          )}
        </div>
      </div>

      {/* Comment text */}
      <div className="mt-3 whitespace-pre-wrap break-words text-pp-text-primary">
        {!isReply && comment.parent_comment_number_id && comment.parent_id && (
          <Link
            href={`/${profileUsername}?tab=forum&comment_id=${comment.parent_id}`}
            shallow
            className="mr-1 text-sys-blue-primary hover:underline"
          >
            {`>>${comment.parent_comment_number_id}`}
          </Link>
        )}
        <RichContent text={trimBreakLines(comment.text)} />
      </div>

      {/* Footer row - single line */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {/* Like button (same style as post reaction bar) */}
        <button
          type="button"
          onClick={handleLike}
          disabled={isLiking}
          className={cn(
            'flex items-center justify-center gap-1 py-2 transition-all duration-200 hover:opacity-70 disabled:opacity-50',
            isLiked ? 'text-gray-900' : 'text-gray-500',
          )}
        >
          <div className="flex size-4 items-center justify-center">
            <LikeIcon
              className={cn(
                'size-4',
                isLiked ? 'fill-pp-green-primary stroke-pp-green-primary' : '',
              )}
            />
          </div>
          <div className="text-sm font-medium">いいね</div>
          {likeCount > 0 && (
            <span className="text-xs font-normal">
              {numberToString(likeCount, 1)}
            </span>
          )}
        </button>

        {/* Reply button with count */}
        <button
          type="button"
          onClick={handleReplyClick}
          className="flex cursor-pointer items-center gap-1.5 rounded-full border border-solid border-gray-300 bg-white px-4 py-1.5 text-sm text-pp-text-primary transition-colors hover:border-sys-blue-primary hover:bg-blue-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-sys-blue-primary"
          >
            <polyline points="9 14 4 9 9 4"></polyline>
            <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
          </svg>
          返信
          <span className="text-pp-text-secondary">
            {comment.reply_count}件
          </span>
        </button>

        {/* Date - aligned right */}
        <span className="ml-auto text-sm text-pp-text-secondary">
          {formattedDate}
        </span>
      </div>

      {/* Confirm delete dialog */}
      <ConfirmDialog
        open={openConfirmDelete}
        title="本当に削除しますか？"
        message=""
        confirmText="削除する"
        cancelText="キャンセル"
        confirmVariant="redDefault"
        onConfirm={() => {
          onDelete(comment.id);
          setOpenConfirmDelete(false);
        }}
        onCancel={() => setOpenConfirmDelete(false)}
      />
    </div>
  );
};

export default ForumCommentItem;
